import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Party, PartyRoleDefinition, PartyAddressTypeDefinition } from '../../services/api/partyService';
import {
  fetchParties, fetchPartyById, createParty, updateParty, deleteParty,
  activateParty, deactivateParty,
  fetchRoleDefinitions, createRoleDefinition, deleteRoleDefinition,
  activateRoleDefinition, deactivateRoleDefinition,
  fetchAddressTypeDefinitions, createAddressTypeDefinition, deleteAddressTypeDefinition,
  activateAddressTypeDefinition, deactivateAddressTypeDefinition,
} from '../thunks/partyThunks';

export interface PartyState {
  parties: Party[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  selectedParty: Party | null;
  roleDefinitions: PartyRoleDefinition[];
  addressTypeDefinitions: PartyAddressTypeDefinition[];
  isLoading: boolean;
  isDefinitionsLoading: boolean;
  error: string | null;
}

const initialState: PartyState = {
  parties: [],
  totalCount: 0,
  pageNumber: 1,
  pageSize: 20,
  selectedParty: null,
  roleDefinitions: [],
  addressTypeDefinitions: [],
  isLoading: false,
  isDefinitionsLoading: false,
  error: null,
};

const partySlice = createSlice({
  name: 'party',
  initialState,
  reducers: {
    clearSelectedParty: (state) => {
      state.selectedParty = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedParty: (state, action: PayloadAction<Party | null>) => {
      state.selectedParty = action.payload;
    },
  },
  extraReducers: (builder) => {
    // --- Fetch Parties ---
    builder
      .addCase(fetchParties.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchParties.fulfilled, (state, action) => {
        state.isLoading = false;
        state.parties = action.payload.parties;
        state.totalCount = action.payload.totalCount;
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchParties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // --- Fetch Party By Id ---
    builder
      .addCase(fetchPartyById.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchPartyById.fulfilled, (state, action) => {
        state.selectedParty = action.payload;
      })
      .addCase(fetchPartyById.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // --- Create Party ---
    builder
      .addCase(createParty.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(createParty.fulfilled, (state) => { state.isLoading = false; })
      .addCase(createParty.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // --- Update Party ---
    builder
      .addCase(updateParty.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(updateParty.fulfilled, (state) => { state.isLoading = false; })
      .addCase(updateParty.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // --- Delete Party ---
    builder
      .addCase(deleteParty.fulfilled, (state, action) => {
        state.parties = state.parties.filter(p => p.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      });

    // --- Activate / Deactivate (optimistic) ---
    builder
      .addCase(activateParty.pending, (state, action) => {
        const party = state.parties.find(p => p.id === action.meta.arg.id);
        if (party) { party.isActive = true; party.status = 'active'; }
      })
      .addCase(activateParty.fulfilled, (state, action) => {
        const party = state.parties.find(p => p.id === action.payload);
        if (party) { party.isActive = true; party.status = 'active'; }
      })
      .addCase(activateParty.rejected, (state, action) => {
        const party = state.parties.find(p => p.id === action.meta.arg.id);
        if (party) { party.isActive = false; party.status = 'inactive'; }
      })
      .addCase(deactivateParty.pending, (state, action) => {
        const party = state.parties.find(p => p.id === action.meta.arg.id);
        if (party) { party.isActive = false; party.status = 'inactive'; }
      })
      .addCase(deactivateParty.fulfilled, (state, action) => {
        const party = state.parties.find(p => p.id === action.payload);
        if (party) { party.isActive = false; party.status = 'inactive'; }
      })
      .addCase(deactivateParty.rejected, (state, action) => {
        const party = state.parties.find(p => p.id === action.meta.arg.id);
        if (party) { party.isActive = true; party.status = 'active'; }
      });

    // --- Role Definitions ---
    builder
      .addCase(fetchRoleDefinitions.pending, (state) => { state.isDefinitionsLoading = true; })
      .addCase(fetchRoleDefinitions.fulfilled, (state, action) => {
        state.isDefinitionsLoading = false;
        state.roleDefinitions = action.payload;
      })
      .addCase(fetchRoleDefinitions.rejected, (state, action) => {
        state.isDefinitionsLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createRoleDefinition.fulfilled, (_state) => { /* refetch after create */ })
      .addCase(deleteRoleDefinition.fulfilled, (state, action) => {
        state.roleDefinitions = state.roleDefinitions.filter(d => d.id !== action.payload);
      });

    // --- Role Definition Activate / Deactivate (optimistic) ---
    builder
      .addCase(activateRoleDefinition.pending, (state, action) => {
        const def = state.roleDefinitions.find(d => d.id === action.meta.arg.id);
        if (def) { def.isActive = true; }
      })
      .addCase(activateRoleDefinition.fulfilled, (state, action) => {
        const def = state.roleDefinitions.find(d => d.id === action.payload);
        if (def) { def.isActive = true; }
      })
      .addCase(activateRoleDefinition.rejected, (state, action) => {
        const def = state.roleDefinitions.find(d => d.id === action.meta.arg.id);
        if (def) { def.isActive = false; }
      })
      .addCase(deactivateRoleDefinition.pending, (state, action) => {
        const def = state.roleDefinitions.find(d => d.id === action.meta.arg.id);
        if (def) { def.isActive = false; }
      })
      .addCase(deactivateRoleDefinition.fulfilled, (state, action) => {
        const def = state.roleDefinitions.find(d => d.id === action.payload);
        if (def) { def.isActive = false; }
      })
      .addCase(deactivateRoleDefinition.rejected, (state, action) => {
        const def = state.roleDefinitions.find(d => d.id === action.meta.arg.id);
        if (def) { def.isActive = true; }
      });

    // --- Address Type Definitions ---
    builder
      .addCase(fetchAddressTypeDefinitions.pending, (state) => { state.isDefinitionsLoading = true; })
      .addCase(fetchAddressTypeDefinitions.fulfilled, (state, action) => {
        state.isDefinitionsLoading = false;
        state.addressTypeDefinitions = action.payload;
      })
      .addCase(fetchAddressTypeDefinitions.rejected, (state, action) => {
        state.isDefinitionsLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createAddressTypeDefinition.fulfilled, (_state) => { /* refetch after create */ })
      .addCase(deleteAddressTypeDefinition.fulfilled, (state, action) => {
        state.addressTypeDefinitions = state.addressTypeDefinitions.filter(d => d.id !== action.payload);
      });

    // --- Address Type Definition Activate / Deactivate (optimistic) ---
    builder
      .addCase(activateAddressTypeDefinition.pending, (state, action) => {
        const def = state.addressTypeDefinitions.find(d => d.id === action.meta.arg.id);
        if (def) { def.isActive = true; }
      })
      .addCase(activateAddressTypeDefinition.fulfilled, (state, action) => {
        const def = state.addressTypeDefinitions.find(d => d.id === action.payload);
        if (def) { def.isActive = true; }
      })
      .addCase(activateAddressTypeDefinition.rejected, (state, action) => {
        const def = state.addressTypeDefinitions.find(d => d.id === action.meta.arg.id);
        if (def) { def.isActive = false; }
      })
      .addCase(deactivateAddressTypeDefinition.pending, (state, action) => {
        const def = state.addressTypeDefinitions.find(d => d.id === action.meta.arg.id);
        if (def) { def.isActive = false; }
      })
      .addCase(deactivateAddressTypeDefinition.fulfilled, (state, action) => {
        const def = state.addressTypeDefinitions.find(d => d.id === action.payload);
        if (def) { def.isActive = false; }
      })
      .addCase(deactivateAddressTypeDefinition.rejected, (state, action) => {
        const def = state.addressTypeDefinitions.find(d => d.id === action.meta.arg.id);
        if (def) { def.isActive = true; }
      });
  },
});

export const { clearSelectedParty, clearError, setSelectedParty } = partySlice.actions;
export default partySlice.reducer;
